import { languages } from "lib/languages"
import { Call, CallState } from "types/payload"
import dayjs from "dayjs"
import { Carousel } from "components/Carousel/Index"
import { cdn } from "core/cdn"
import FatButton from "components/Button/FatButton/FatButton"
import React, { useState } from "react"
import { ModalDocument } from "client/documents/utils"
import { Buttons } from "components/Card/Card"
import { Button } from "components/Button"
import { Important } from "components/Title/Title"
import { Customer } from "types/entity"
import { useSelector } from "react-redux"
import { GlobalStore } from "types/store"

interface HistoryProps {
  calls: Call[]
}

const fullName = (patient?: Pick<Customer, "firstname" | "lastname">): string =>
  patient ? `${patient.firstname} ${patient.lastname}`.toLowerCase() : ""

const CallDisplayer: React.FC<
  Call & { onSelect: () => void; patientName: string }
> = (props) => {
  const callStateLabel = {
    [CallState.CANCELLED_BY_DOCTOR]: languages.callCancelled,
    [CallState.CANCELLED_BY_PATIENT]: languages.callCancelled,
    [CallState.ENDED]: languages.callEnded,
    [CallState.EXPIRED]: languages.callExpired,
  }[props.state] || ""
  const callStateLabelColor = [
    CallState.CANCELLED_BY_DOCTOR,
    CallState.CANCELLED_BY_PATIENT,
    CallState.EXPIRED,
  ].includes(props.state)
    ? "#D92A21"
    : "#1D71B8"
  const date = dayjs(props.created_at)
  return (
    <FatButton
      onClick={props.onSelect}
      doNotFormat
      img={
        props.state === CallState.CANCELLED_BY_DOCTOR
          ? cdn("icons/camera_canceled_picto.svg")
          : cdn("icons/camera_picto.svg")
      }
      imgStyle={{ width: "68px", objectFit: "contain" }}
    >
      <div
        style={{
          fontSize: "20px",
          lineHeight: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <span style={{ color: callStateLabelColor }}>
          {callStateLabel}
        </span>
        <div>
          {languages.tlcOf} <b>{date.format("DD/MM/YYYY")}</b> à{" "}
          <b>{date.format("HH:mm")}</b>
        </div>
        <b style={{ textTransform: "capitalize" }}>{props.patientName}</b>
      </div>
    </FatButton>
  )
}

export const History: React.FC<HistoryProps> = (props): JSX.Element => {
  const [selectedCall, setSelectedCall] = useState<Call | undefined>()
  const customer = useSelector((state: GlobalStore) => state.client?.customer)
  const relatives = useSelector((state: GlobalStore) => state.client?.relatives)

  const patientName = (call: Call): string => {
    if (call.relative_id != null) {
      return fullName(relatives?.find((relative) => relative.id === call.relative_id))
    }
    return fullName(customer)
  }

  const items = props.calls
    .filter(
      (call) =>
        ![
          CallState.CANCELLED_BY_PATIENT,
          CallState.EXPIRED,
        ].includes(call.state)
    )
    .map((call, key) => (
      <CallDisplayer
        key={key}
        {...call}
        onSelect={() => setSelectedCall(call)}
        patientName={patientName(call)}
      />
    ))
  return (
    <>
      {items.length ? (
        <Carousel items={items} />
      ) : (
        <Buttons>
          <Important text={languages.noTLC} />
        </Buttons>
      )}

      <ModalDocument
        call={selectedCall}
        footer={
          <Buttons>
            <Button onClick={() => setSelectedCall(undefined)}>
              {languages.previous}
            </Button>
          </Buttons>
        }
        onCancel={() => setSelectedCall(undefined)}
        acceptText={languages.cancel}
      />
    </>
  )
}
